import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Capacitor } from '@capacitor/core';
import {
    PushNotifications,
    Token,
    PushNotificationSchema,
    ActionPerformed,
} from '@capacitor/push-notifications';
import { environment } from 'src/environments/environment';
import { NativeNotificationService } from './native-notification.service';

@Injectable({
    providedIn: 'root',
})
export class PushTokenService {
    private initialized = false;
    private currentToken = '';

    constructor(
        private http: HttpClient,
        private router: Router,
        private nativeNotifications: NativeNotificationService
    ) {}

    isNativeDevice(): boolean {
        return Capacitor.isNativePlatform();
    }

    async init(): Promise<void> {
        if (!this.isNativeDevice() || this.initialized) {
            return;
        }

        this.initialized = true;

        await PushNotifications.addListener('registration', (token: Token) => {
            if (!this.shouldSyncToken(token.value)) {
                return;
            }

            this.currentToken = token.value;
            this.registerTokenWithBackend().catch((error) => {
                console.error('Failed to sync push token:', error);
            });
        });

        await PushNotifications.addListener('registrationError', (error: any) => {
            console.error('Push registration failed:', error);
        });

        await PushNotifications.addListener(
            'pushNotificationReceived',
            (notification: PushNotificationSchema) => {
                this.handleIncomingPush(notification);
            }
        );

        await PushNotifications.addListener(
            'pushNotificationActionPerformed',
            (action: ActionPerformed) => {
                this.handlePushAction(action);
            }
        );
    }

    async registerCurrentDevice(): Promise<void> {
        if (!this.isNativeDevice()) {
            return;
        }

        await this.init();

        const permission = await PushNotifications.requestPermissions();
        if (permission.receive !== 'granted') {
            console.error('Push notification permission not granted');
            return;
        }

        await PushNotifications.register();
    }

    async unregisterCurrentDevice(): Promise<void> {
        if (!this.isNativeDevice()) {
            return;
        }

        if (this.currentToken) {
            try {
                await this.http
                    .delete(`${environment.api_url}/mobile-device-token`, {
                        body: { device_token: this.currentToken },
                    })
                    .toPromise();
            } catch (error) {
                console.error('Failed to delete push token:', error);
            }
        }

        try {
            await PushNotifications.unregister();
        } catch (error) {
            console.error('Failed to unregister push notifications:', error);
        }
    }

    private async registerTokenWithBackend(): Promise<void> {
        if (!this.currentToken) {
            return;
        }

        const payload = {
            platform: Capacitor.getPlatform(),
            device_token: this.currentToken,
            device_name: navigator.userAgent || 'mobile-device',
            app_version: 'mobile',
        };

        await this.http.post(`${environment.api_url}/mobile-device-token`, payload).toPromise();
    }

    private shouldSyncToken(token: string): boolean {
        if (Capacitor.getPlatform() !== 'ios') {
            return true;
        }

        // Firebase Messaging will publish the FCM token shortly after APNs registration.
        // Ignore the raw APNs hex token so the backend stores the token format Firebase expects.
        return !/^[A-F0-9]{64,}$/i.test(token);
    }

    private handleIncomingPush(notification: PushNotificationSchema): void {
        const data = notification?.data || {};
        const action = `${data['action'] || ''}`.toLowerCase();
        const type = `${data['type'] || ''}`.toLowerCase();
        const body = notification?.body || `${data['body'] || data['message'] || ''}`;

        if (action === 'clear_alerts') {
            this.nativeNotifications.clearAlertNotifications();
            return;
        }

        if (type === 'call') {
            return;
        }

        if (type === 'email') {
            this.nativeNotifications.notifyAlert(
                body || 'New email received',
                'email',
                data['conversation_id']
            );
            return;
        }

        if (type === 'sms') {
            this.nativeNotifications.notifyAlert(
                body || 'New text received',
                'sms',
                data['conversation_id']
            );
        }
    }

    private handlePushAction(action: ActionPerformed): void {
        const data = action?.notification?.data || {};
        const type = `${data['type'] || ''}`.toLowerCase();
        const route = `${data['route'] || ''}`;
        const conversationId = this.parseConversationId(data['conversation_id']);

        if (conversationId) {
            this.router
                .navigate(['conversations'], {
                    queryParams: { id: conversationId },
                })
                .catch((error) => {
                    console.error('Failed to route push action to conversation:', error);
                });
            return;
        }

        if (route) {
            this.router.navigate([route]).catch((error) => {
                console.error('Failed to route push action:', error);
            });
            return;
        }

        if (type === 'call') {
            this.router.navigate(['conversations']).catch((error) => {
                console.error('Failed to route call push action:', error);
            });
            return;
        }

        this.router.navigate(['alerts']).catch((error) => {
            console.error('Failed to route alert push action:', error);
        });
    }

    private parseConversationId(value: string | number | null | undefined): number | null {
        if (value === null || value === undefined || value === '') {
            return null;
        }

        const parsed = Number(value);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    }
}
